import { LocationQueryRaw, NavigationGuard, RouteLocationNormalized, RouteParamsRaw, RouteRecordName } from 'vue-router';
import { Module } from 'vuex';
import { RootState } from './states';

export class LoadError extends Error {
  constructor(public reason: any) {
    super(`Load error: ${reason}`);
  }
}

export interface RouteLocationOverride {
  name?: RouteRecordName;
  params?: RouteParamsRaw;
  query?: LocationQueryRaw;
}

export class RedirectDuringLoad extends LoadError {
  constructor(public handle: NavigationGuard) {
    super('redirect during load');
  }

  static to(locationOverride: RouteLocationOverride): RedirectDuringLoad {
    return new RedirectDuringLoad((to, from, next) => {
      next({
        name: locationOverride.name ?? to.name ?? undefined,
        query: {
          ...(to.query ?? {}),
          ...(locationOverride.query ?? {}),
        },
        params: {
          ...(to.params ?? {}),
          ...(locationOverride.params ?? {}),
        },
        hash: to.hash,
      });
    });
  }
}

export function wrapRedirectHandler(action: (to: RouteLocationNormalized, from: RouteLocationNormalized) => any): NavigationGuard {
  return async (to, from, next) => {
    try {
      await action(to, from);
      next();
    } catch (error) {
      if (error instanceof RedirectDuringLoad) {
        error.handle(to, from, next);
      } else if (error instanceof Error) {
        next(error);
      } else {
        console.error('unexpected error type', error); // eslint-disable-line no-console
        next();
      }
    }
  };
}

interface LoadPayload {
  actions?: Array<string>;
  params?: { [key: string]: string };
}

const module: Module<{}, RootState> = {
  namespaced: true,

  state: {},

  mutations: {},

  actions: {
    async load({ dispatch }, { actions, params }: LoadPayload) {
      if (Array.isArray(actions) && actions.length > 0) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('Load:', actions);
        }
        const promises = actions.map(action => dispatch(action, params, { root: true }));
        const results = await Promise.allSettled(promises);
        const errors = results.map(r => r.status === 'rejected' && r.reason).filter(Boolean);
        const primaryError = errors.find(e => e instanceof RedirectDuringLoad) || errors[0];

        if (import.meta.env.DEV && errors.length > 1) {
          // NOTE we don't log additional errors in production because this may be "normal" behaviour
          // e.g. simultaneous redirects: the first one will be handled immediately, additional ones only once they reoccur
          const otherErrors = errors.filter(e => e !== primaryError);
          // eslint-disable-next-line no-console
          console.warn('Additional errors occurred during load', otherErrors);
        }

        if (primaryError) throw primaryError instanceof LoadError ? primaryError : new LoadError(primaryError);
      }
    },

    unload({ commit }, [mutations, route]: [Array<any>, RouteLocationNormalized]) {
      if (Array.isArray(mutations) && mutations.length > 0) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('Unload:', mutations);
        }
        mutations.forEach(mutation => typeof mutation === 'function' ? mutation(route) : commit(mutation, undefined, { root: true }));
      }
    },
  },
};

export default module;
